import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTag, FaStar, FaTimes, FaUpload } from 'react-icons/fa';
import { brandService } from '../../services/brandService';
import { uploadService } from '../../services/uploadService';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const BrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', logo: { url: '', public_id: '' } });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBrands, setFilteredBrands] = useState([]);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = brands.filter(brand => 
        brand.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    } else {
      setFilteredBrands(brands);
    }
  }, [searchTerm, brands]);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await brandService.getBrands();
      setBrands(response.data);
      setFilteredBrands(response.data);
    } catch (error) {
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter brand name');
      return;
    }

    const loadingToast = toast.loading(editingBrand ? 'Updating brand...' : 'Creating brand...');
    
    try {
      if (editingBrand) {
        await brandService.updateBrand(editingBrand._id, formData);
        toast.success('Brand updated successfully!', { id: loadingToast });
      } else {
        await brandService.createBrand(formData);
        toast.success('Brand created successfully!', { id: loadingToast });
      }
      setShowModal(false);
      setEditingBrand(null);
      setFormData({ name: '', logo: { url: '', public_id: '' } });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save brand', { id: loadingToast });
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, logo: brand.logo || { url: '', public_id: '' } });
    setShowModal(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const toastId = toast.loading('Uploading brand logo...');

    try {
      const response = await uploadService.uploadBrandLogo(file);
      setFormData(prev => ({
        ...prev,
        logo: {
          url: response.data.url,
          public_id: response.data.public_id,
        },
      }));
      toast.success('Logo uploaded successfully', { id: toastId });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload logo', { id: toastId });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: { url: '', public_id: '' }
    }));
    toast.success('Logo removed');
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this brand? Products associated with this brand will be affected.');
    if (!confirmed) return;

    const loadingToast = toast.loading('Deleting brand...');
    
    try {
      await brandService.deleteBrand(id);
      toast.success('Brand deleted successfully', { id: loadingToast });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete brand', { id: loadingToast });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading brands...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Brand Management</h1>
            <p className="text-primary-100">Manage your product brands and manufacturers</p>
          </div>
          <button
            onClick={() => {
              setEditingBrand(null);
              setFormData({ name: '', logo: { url: '', public_id: '' } });
              setShowModal(true);
            }}
            className="bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center gap-2 shadow-lg"
          >
            <FaPlus /> Add Brand
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-4">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search brands by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
          />
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaTag className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Brands</p>
              <p className="text-2xl font-bold">{brands.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaStar className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Featured Brands</p>
              <p className="text-2xl font-bold">{brands.filter(b => b.featured).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="text-2xl opacity-80">📦</div>
            <div>
              <p className="text-sm opacity-90">With Products</p>
              <p className="text-2xl font-bold">{brands.filter(b => b.productCount > 0).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Brands Grid */}
      {filteredBrands.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-12 text-center">
          <div className="text-6xl mb-4">🏷️</div>
          <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">No brands found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm ? 'Try a different search term' : 'Get started by creating your first brand'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => {
                setEditingBrand(null);
                setFormData({ name: '', logo: { url: '', public_id: '' } });
                setShowModal(true);
              }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <FaPlus /> Create Brand
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredBrands.map((brand, index) => (
              <motion.div
                key={brand._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft hover:shadow-hard transition-all duration-300 overflow-hidden group"
              >
                {/* Brand Header */}
                <div className="relative h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                  {brand.logo?.url ? (
                    <img 
                      src={brand.logo.url} 
                      alt={brand.name} 
                      className="max-h-24 max-w-full object-contain p-4"
                    />
                  ) : (
                    <div className="text-4xl opacity-30">🏷️</div>
                  )}
                  <div className="absolute top-3 right-3 flex gap-2">
                    <button
                      onClick={() => handleEdit(brand)}
                      className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-blue-600 hover:bg-white transition-colors shadow-sm"
                      title="Edit brand"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(brand._id)}
                      className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-red-600 hover:bg-white transition-colors shadow-sm"
                      title="Delete brand"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                  {brand.featured && (
                    <div className="absolute top-3 left-3 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <FaStar size={10} /> Featured
                    </div>
                  )}
                </div>

                {/* Brand Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-1">
                        {brand.name}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Slug: /{brand.slug}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Products:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {brand.productCount || 0}
                      </span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      brand.isActive 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {brand.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">
                    {editingBrand ? '✏️ Edit Brand' : '➕ Add New Brand'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
                <p className="text-primary-100 text-sm mt-1">
                  {editingBrand ? 'Update brand information' : 'Fill in the details to create a new brand'}
                </p>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-5">
                  {/* Brand Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Brand Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Apple, Samsung, Xiaomi"
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
                      required
                    />
                  </div>

                  {/* Brand Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Brand Logo
                    </label>
                    {formData.logo?.url ? (
                      <div className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900 flex flex-col items-center">
                        <img
                          src={formData.logo.url}
                          alt="Brand Logo Preview"
                          className="h-32 object-contain rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors flex items-center gap-1.5 shadow-md"
                          >
                            <FaTrash size={12} /> Remove Logo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-6 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors bg-gray-50 dark:bg-gray-900/50">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          id="logo-upload"
                          className="hidden"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="cursor-pointer inline-flex flex-col items-center w-full"
                        >
                          <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-soft mb-3 border border-gray-100 dark:border-gray-700">
                            {uploadingLogo ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                            ) : (
                              <FaUpload className="text-xl text-gray-500 dark:text-gray-400" />
                            )}
                          </div>
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            {uploadingLogo ? 'Uploading logo...' : 'Upload Brand Logo'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG or JPEG up to 5MB
                          </p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex gap-4 mt-8">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all flex items-center justify-center gap-2"
                  >
                    {editingBrand ? 'Update Brand' : 'Create Brand'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBrand(null);
                      setFormData({ name: '', logo: { url: '', public_id: '' } });
                    }}
                    className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrandManagement;
